import { useState } from 'react';
import type { Album, Grant } from '../lib/api';
import { signedUrl } from '../lib/api';
import { CoverArt } from './CoverArt';
import { NoteIcon } from './icons';

type Props = {
  albums: Album[];
  selectedId: string | null;
  grant: Grant | null;
  onSelect: (albumId: string) => void;
  onCreate: () => void;
  onReorder: (order: string[]) => void;
};

export function AlbumList({ albums, selectedId, grant, onSelect, onCreate, onReorder }: Props) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const drop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) return;
    const next = [...albums];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    onReorder(next.map((a) => a.albumId));
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-head">
        <h2>Albums</h2>
        <div className="spacer" />
        <button className="tiny" onClick={onCreate} title="Create an album">+ New</button>
      </div>
      <div className="album-scroll">
        {albums.length === 0 && (
          <p className="hint" style={{ padding: '8px 8px 0' }}>No albums yet. Create one.</p>
        )}
        {albums.map((album, index) => (
          <button
            key={album.albumId}
            className={[
              'album-item',
              album.albumId === selectedId ? 'active' : '',
              overIndex === index ? 'drag-over' : '',
            ].filter(Boolean).join(' ')}
            onClick={() => onSelect(album.albumId)}
            draggable
            onDragStart={() => setDragIndex(index)}
            onDragOver={(e) => { e.preventDefault(); setOverIndex(index); }}
            onDragLeave={() => setOverIndex((i) => (i === index ? null : i))}
            onDrop={(e) => { e.preventDefault(); drop(index); }}
            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
          >
            {album.coverKey ? (
              <CoverArt
                className="album-cover"
                src={signedUrl(grant, album.coverKey.replace(/^audio\//, ''))}
              />
            ) : (
              <div className="album-cover"><NoteIcon /></div>
            )}
            <div className="album-meta">
              <div className="album-name">{album.name}</div>
              <div className="album-sub">
                {album.status === 'published' ? 'Published' : 'Draft'}
              </div>
            </div>
          </button>
        ))}
      </div>
    </aside>
  );
}
